import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import ElementaryProgressReportDao from "../dao/ElementaryProgressReportDao";

import { byOrdinal, normalizeRatingForCompare } from "../utils/teacher/InputRatingUtils";

export function sleRatingValuesMatch(existingRating, incomingUiRating) {
    return (
        existingRating != null &&
        String(normalizeRatingForCompare(existingRating) ?? "") ===
            String(normalizeRatingForCompare(incomingUiRating) ?? "")
    );
}

function resolveSleLastRaterGuid(unchanged, existingLastRaterGuid, userGuid) {
    return unchanged ? (existingLastRaterGuid ?? userGuid) : userGuid;
}

function appendSleSaveRowsForOneSle(rows, sle, ctx) {
    const { sleValuesByKey, sleRatings, findExistingSleKey, gradingProfileStudentId, userGuid } = ctx;
    const existingTopRow = sleRatings?.find((r) => Number(r.sleId) === Number(sle.key));

    const topKey = `SLE:${sle.key}`;
    const topRating = sleValuesByKey[topKey];
    if (Object.hasOwn(sleValuesByKey, topKey)) {
        const topUnchanged = existingTopRow && sleRatingValuesMatch(existingTopRow.rating, topRating);
        const lastRaterGuidTop = resolveSleLastRaterGuid(topUnchanged, existingTopRow?.lastRaterGuid, userGuid);
        rows.push({
            key: findExistingSleKey(sle.key, null),
            sleId: Number(sle.key),
            sleChildId: null,
            gradingProfileStudentId: Number(gradingProfileStudentId),
            rating: topRating === "DOES NOT MEET" ? "DOES_NOT_MEET" : topRating,
            personalizedLearningPlan: false,
            lastRaterGuid: lastRaterGuidTop
        });
    }
    const children = [...(sle.sleChildList || [])].sort(byOrdinal);
    for (const child of children) {
        const childKey = `SLEC:${child.key}`;
        const childRating = sleValuesByKey[childKey];
        if (!Object.hasOwn(sleValuesByKey, childKey)) continue;
        const existingChildRow = (existingTopRow?.sleChildRatings || []).find(
            (c) => Number(c.sleChildId) === Number(child.key)
        );
        const childUnchanged = existingChildRow && sleRatingValuesMatch(existingChildRow.rating, childRating);
        const lastRaterGuidChild = resolveSleLastRaterGuid(childUnchanged, existingChildRow?.lastRaterGuid, userGuid);
        rows.push({
            key: findExistingSleKey(sle.key, child.key),
            sleId: Number(sle.key),
            sleChildId: Number(child.key),
            gradingProfileStudentId: Number(gradingProfileStudentId),
            rating: childRating === "DOES NOT MEET" ? "DOES_NOT_MEET" : childRating,
            personalizedLearningPlan: false,
            lastRaterGuid: lastRaterGuidChild
        });
    }
}

export function buildSleRatingSaveRows({
    sortedSles,
    sleValuesByKey,
    sleRatings,
    findExistingSleKey,
    gradingProfileStudentId,
    userGuid
}) {
    const rows = [];
    const ctx = { sleValuesByKey, sleRatings, findExistingSleKey, gradingProfileStudentId, userGuid };
    for (const sle of sortedSles) {
        appendSleSaveRowsForOneSle(rows, sle, ctx);
    }
    return rows;
}

/**
 * Handles School Level Expectations (SLE) data lifecycle:
 * - Fetch student SLE ratings
 * - Fetch School Level Expectations for the student's school/year
 * - Prime local UI map (sleValuesByKey) from ratings
 * - Expose change handlers + a save method that emits your expected payload
 * *
 * @typedef {Object} SleRatingsParams
 * @property {string} incomingTemplateId
 * @property {Object} student
 * @property {Object} studentProfileData
 * @property {number} userGuid
 * @property {string} token
 * @param {SleRatingsParams} params
 * @returns {Object} Hook API for SLE operations
 */

export default function useSleRatings({ incomingTemplateId, student, studentProfileData, userGuid, token }) {
    const [sleRatings, setSleRatings] = useState([]);
    const [schoolLevelExpectations, setSchoolLevelExpectations] = useState([]);
    const [sleValuesByKey, setSleValuesByKey] = useState({});
    const [loading, setLoading] = useState(false);
    const [sleComment, setSleComment] = useState("");
    const [sleCommentKey, setSleCommentKey] = useState(null);
    // Ignore stale getSleRatingComment / save responses after student switch.
    const sleCommentFetchKeyRef = useRef(null);
    const displayedStudentIdRef = useRef(student?.gradingProfileStudentId ?? null);
    displayedStudentIdRef.current = student?.gradingProfileStudentId ?? null;

    // Clear SLE comment when the student changes so stale text is never shown/saved.
    useEffect(() => {
        const gpsId = student?.gradingProfileStudentId;
        setSleComment("");
        setSleCommentKey(null);
        sleCommentFetchKeyRef.current = gpsId != null ? String(gpsId) : null;
    }, [student?.gradingProfileStudentId]);

    // Fetch existing SLE ratings for this student
    useEffect(() => {
        if (incomingTemplateId !== "SLE" || !student?.gradingProfileStudentId) return;
        const fetchForStudentId = student.gradingProfileStudentId;
        setLoading(true);
        ElementaryProgressReportDao({
            action: "getSleRatings",
            gradingProfileStudentId: fetchForStudentId,
            token
        })
            .then((res) => {
                if (String(displayedStudentIdRef.current) !== String(fetchForStudentId)) return;
                if (res?.data?.payload) setSleRatings(res.data.payload);
            })
            .catch((error) => {
                if (String(displayedStudentIdRef.current) !== String(fetchForStudentId)) return;
                console.error(error);
                toast.error("There was a problem loading School Level Expectation ratings. Please try again later.");
            })
            .finally(() => {
                if (String(displayedStudentIdRef.current) === String(fetchForStudentId)) {
                    setLoading(false);
                }
            });
    }, [incomingTemplateId, student?.gradingProfileStudentId, token]);

    // Fetch SLE list for location/year
    useEffect(() => {
        if (!studentProfileData) return;
        const { schoolYearKey, locationId } = studentProfileData || {};
        if (!schoolYearKey || !locationId) return;

        setLoading(true);
        ElementaryProgressReportDao({
            action: "getSchoolLevelExpectations",
            schoolYearKey,
            locationKey: locationId,
            token
        })
            .then((res) => {
                if (res?.data?.payload) setSchoolLevelExpectations(res.data.payload);
            })
            .catch((error) => {
                console.error(error);
                toast.error("There was a problem loading School Level Expectation ratings. Please try again later.");
            })
            .finally(() => setLoading(false));
    }, [incomingTemplateId, studentProfileData, token]);

    // Prime map (SLE:<sleId>, SLEC:<sleChildId>) from sleRatings
    useEffect(() => {
        if (incomingTemplateId !== "SLE") return;
        const next = {};
        for (const r of sleRatings || []) {
            next[`SLE:${r.sleId}`] = r.rating === "DOES_NOT_MEET" ? "DOES NOT MEET" : r.rating || null;
            for (const c of r.sleChildRatings || []) {
                next[`SLEC:${c.sleChildId}`] = c.rating === "DOES_NOT_MEET" ? "DOES NOT MEET" : c.rating || null;
            }
        }
        setSleValuesByKey(next);
    }, [incomingTemplateId, sleRatings]);

    // Fetch SLE top-level comment for this student
    useEffect(() => {
        const gradingProfileStudentId = student?.gradingProfileStudentId;
        if (incomingTemplateId !== "SLE" || !gradingProfileStudentId) return;
        const fetchKey = String(gradingProfileStudentId);
        sleCommentFetchKeyRef.current = fetchKey;
        (async () => {
            try {
                const res = await ElementaryProgressReportDao({
                    action: "getSleRatingComment",
                    gradingProfileStudentId,
                    token
                });
                if (sleCommentFetchKeyRef.current !== fetchKey) return;
                const payload = res?.data?.payload;
                // Payload can be an object or array — handle defensively
                const commentObj = Array.isArray(payload) ? payload[0] : payload;
                setSleComment(commentObj?.comment || "");
                setSleCommentKey(Number.isFinite(Number(commentObj?.key)) ? Number(commentObj.key) : null);
            } catch (e) {
                if (sleCommentFetchKeyRef.current !== fetchKey) return;
                toast.error(
                    "There was a problem loading the School Level Expectations comment. Please try again later."
                );
                console.error("Failed to fetch SLE comment", e);
            }
        })();
    }, [incomingTemplateId, student?.gradingProfileStudentId, token]);

    const onChangeSleRatingByKey = useCallback((uiKey, newVal) => {
        setSleValuesByKey((prev) => ({ ...prev, [uiKey]: newVal || null }));
    }, []);

    // Lookup existing DB keys so backend can update vs create
    const findExistingSleKey = useCallback(
        (sleId, sleChildId) => {
            const top = sleRatings?.find((r) => Number(r.sleId) === Number(sleId));
            if (!top) return null;
            if (sleChildId == null) return Number(top.key) || null;
            const child = (top.sleChildRatings || []).find((c) => Number(c.sleChildId) === Number(sleChildId));
            return child ? Number(child.key) || null : null;
        },
        [sleRatings]
    );

    // Sorted projections (by ordinal) to keep UI stable
    const sortedSles = useMemo(() => [...(schoolLevelExpectations || [])].sort(byOrdinal), [schoolLevelExpectations]);

    const sleTotalCount = useMemo(() => {
        const list = Array.isArray(sortedSles) ? sortedSles : [];
        return list.reduce((acc, sle) => {
            const childCount = Array.isArray(sle.sleChildList) ? sle.sleChildList.length : 0;
            return acc + 1 + childCount;
        }, 0);
    }, [sortedSles]);

    const sleRatedCount = useMemo(() => {
        if (!sleValuesByKey) return 0;
        return Object.values(sleValuesByKey).filter((v) => v && v !== "UNRATED").length;
    }, [sleValuesByKey]);

    const saveSleRatings = useCallback(async () => {
        const gradingProfileStudentId = student?.gradingProfileStudentId;
        if (!gradingProfileStudentId) return;
        const savedForStudentId = gradingProfileStudentId;

        const rows = buildSleRatingSaveRows({
            sortedSles,
            sleValuesByKey,
            sleRatings,
            findExistingSleKey,
            gradingProfileStudentId: savedForStudentId,
            userGuid
        });

        const options = {
            action: "updateSleRating",
            gradingProfileStudentId: savedForStudentId,
            data: rows,
            token
        };

        try {
            const response = await ElementaryProgressReportDao(options);
            if (String(displayedStudentIdRef.current) !== String(savedForStudentId)) return;
            if (response?.data?.payload) {
                setSleRatings(response?.data?.payload);
            }
            toast.success("School level expectations successfully saved.");
        } catch (e) {
            console.error("Ratings were not saved. Please try again later.", e);
            if (String(displayedStudentIdRef.current) === String(savedForStudentId)) {
                toast.error("There was a problem saving School Level Expectation ratings. Please try again later.");
            }
        }
    }, [findExistingSleKey, sleRatings, sleValuesByKey, sortedSles, student?.gradingProfileStudentId, token, userGuid]);

    // Save (create/update) SLE comment. Empty string is allowed; delete uses explicit null.
    const saveSleComment = useCallback(async () => {
        const gradingProfileStudentId = student?.gradingProfileStudentId;
        if (!gradingProfileStudentId) return;
        const savedForStudentId = gradingProfileStudentId;
        // Capture comment + key at save start so a mid-flight student switch does not
        // rewrite the in-flight payload with the newly displayed student's state.
        const commentAtSave = sleComment;
        const commentKeyAtSave = sleCommentKey;
        setLoading(true);
        try {
            const options = {
                action: "updateSleRatingComment",
                data: {
                    key: commentKeyAtSave ?? null,
                    gradingProfileStudentId: savedForStudentId,
                    comment: commentAtSave, // string (may be empty)
                    lastUpdaterGuid: userGuid
                },
                token
            };

            const res = await ElementaryProgressReportDao(options);

            // Student switcher: never apply a stale save onto the newly displayed student.
            if (String(displayedStudentIdRef.current) !== String(savedForStudentId)) return;

            // Assume backend returns the updated/created comment object or array
            const payload = res?.data?.payload;

            if (payload) {
                // Keep local state in sync with DB
                setSleComment(payload.comment ?? "");

                // Capture the key so next save is an update, not a create
                if (payload.key != null) {
                    const numericKey = Number(payload.key);
                    setSleCommentKey(Number.isFinite(numericKey) ? numericKey : payload.key);
                }
            }

            toast.success("Comment successfully saved.");
        } catch (e) {
            console.error("Failed to save SLE comment", e);
            if (String(displayedStudentIdRef.current) === String(savedForStudentId)) {
                toast.error("Comment was not saved. Please make note of your comment and try again later.");
            }
        } finally {
            if (String(displayedStudentIdRef.current) === String(savedForStudentId)) {
                setLoading(false);
            }
        }
    }, [sleComment, sleCommentKey, student?.gradingProfileStudentId, token, userGuid]);

    return {
        loading,
        sortedSles,
        sleValuesByKey,
        onChangeSleRatingByKey,
        saveSleRatings,
        sleTotalCount,
        sleRatedCount,
        sleComment,
        setSleComment,
        saveSleComment
    };
}
